import React, { useEffect, useMemo, useState } from 'react';

import { Typography } from '@mui/material';
import type {
  GridColDef,
  GridInitialState,
  GridRenderCellParams,
  GridRowProps,
  GridRowSelectionModel,
  GridToolbarProps,
} from '@mui/x-data-grid-premium';
import { DataGridPremium, GridRow, useGridApiRef } from '@mui/x-data-grid-premium';
import clsx from 'clsx';
import {
  AlineCheckbox,
  AlineTooltip,
  allPartyFilters,
  FiltersToolbarButton,
  getIsGridState,
  ListCountFooter,
  ListEmpty,
  ReportFiltersPanel,
  SearchInput,
  Spacer,
  StorableIcon,
} from 'src/components';
import { DocumentSearchIcon } from 'src/components/icons/DocumentSearchIcon';
import type { Apollo } from 'src/gql/generated';
import { doNothing } from 'src/helpers';
import {
  createDataGridPremiumOverrideStyles,
  createDataGridPremiumPaperStyles,
  defaultFilterModel,
  FiltersAnchorProvider,
  useDataGridSearch,
  useFiltersAnchorState,
  useReportDataSource,
} from 'src/hooks';
import type { FileVersionSubset, UserSubset } from 'src/types';
import { createClampStyles } from 'src/utils/styles';
import { makeStyles } from 'tss-react/mui';

const rowHeightPx = 52;
const useStyles = makeStyles()(() => ({
  emptyIcon: {
    height: 100,
    width: 100,
  },
  listContainer: {
    // Flex into the parent so dialog callers can constrain picker height and
    // keep their footers (e.g. Cancel/Create) visible on smaller viewports.
    // `minHeight: 0` lets the grid shrink past its children's intrinsic size.
    display: 'flex',
    flex: '1 1 0',
    flexDirection: 'column' as const,
    minHeight: rowHeightPx * 4,
  },
  rowDisabled: {
    opacity: 0.5,
  },
  xGrid: {
    flexGrow: 1,
  },
}));

export enum DocumentPickerVariant {
  SingleSelect,
  MultiSelect,
}

interface RowDisabledState {
  isRowDisabled: boolean;
  reason: string;
}

const ROW_ENABLED: RowDisabledState = { isRowDisabled: false, reason: '' };
const ROW_NOT_LOADED: RowDisabledState = { isRowDisabled: true, reason: '' };

export interface DocumentPickerRow extends Pick<
  Apollo.Document,
  'createdAt' | 'id' | 'lastAccessedByRequesterAt' | 'name' | '__typename' | 'variant'
> {
  latestVersion: FileVersionSubset;
  owner: UserSubset;
  parties: Pick<Apollo.Party, 'id' | 'name' | '__typename'>[];
  status: Apollo.DocumentStatus;
  type: Pick<Apollo.ContractType, 'id' | '__typename' | 'value'> | null;
  workflow: Pick<Apollo.Workflow, 'id' | 'name' | '__typename' | 'variant'> | null;
}

interface DocumentPickerProps extends React.HTMLAttributes<HTMLElement> {
  repositoryId: UUID;
  documentVariant?: Apollo.DocumentVariant;
  extraColumns?: GridColDef<DocumentPickerRow>[];
  extraColumnsAfterField?: string;
  extraToolbarContentLeft?: React.ReactNode;
  extraToolbarContentRight?: React.ReactNode;
  getIsRowDisabled?: (row: DocumentPickerRow) => RowDisabledState;
  onSelectionChange?: (documentIds: UUID[]) => void;
  pickerVariant?: DocumentPickerVariant;
  searchPlaceholder?: string;
  selectedDocumentIds?: UUID[];
  showDocumentCountFooter?: boolean;
  showFiltersButton?: boolean;
}

export function DocumentPicker({
  className,
  documentVariant,
  extraColumns = [],
  extraColumnsAfterField = 'type',
  extraToolbarContentLeft,
  extraToolbarContentRight,
  getIsRowDisabled = () => ROW_ENABLED,
  onSelectionChange = doNothing,
  pickerVariant = DocumentPickerVariant.MultiSelect,
  repositoryId,
  searchPlaceholder,
  selectedDocumentIds,
  showDocumentCountFooter = false,
  showFiltersButton = false,
}: DocumentPickerProps): JSX.Element {
  const { classes } = useStyles();
  const apiRef = useGridApiRef();
  const { anchorEl: filtersAnchorEl, setAnchorEl: setFiltersAnchorEl } = useFiltersAnchorState();
  const [selectedDocIds, setSelectedDocIds] = useState<GridRowSelectionModel>({
    ids: new Set(selectedDocumentIds ?? []),
    type: 'include',
  });
  const mockReport = useMemo<Pick<Apollo.Report, 'state'>>(
    () => ({
      state: {
        filter: { filterModel: defaultFilterModel },
        sorting: {
          sortModel: [
            {
              field: 'lastAccessedByRequesterAt' satisfies keyof DocumentPickerRow,
              sort: 'desc',
            },
          ],
        },
      } satisfies GridInitialState,
    }),
    []
  );
  const {
    dataSource,
    documentCount,
    filterModelTuple: [filterModel, setFilterModel],
    onDataSourceError,
    sortModelTuple: [sortModel, setSortModel],
  } = useReportDataSource(repositoryId, {
    apiRef,
    docVariant: documentVariant,
    report: mockReport,
  });
  const colDefs = useMemo((): GridColDef<DocumentPickerRow>[] => {
    const baseCols: GridColDef<DocumentPickerRow>[] = [
      {
        field: 'name',
        flex: 2,
        headerName: 'Title',
        renderCell: (
          params: GridRenderCellParams<DocumentPickerRow, DocumentPickerRow['name']>
        ) => <NameCell {...params} />,
      },
      {
        field: 'type',
        flex: 1,
        headerName: 'Type',
        renderCell: ({
          value: contractType,
        }: GridRenderCellParams<DocumentPickerRow, DocumentPickerRow['type']>) => {
          return <Typography>{contractType?.value ?? '-'}</Typography>;
        },
        sortComparator: (v1: DocumentPickerRow['type'], v2: DocumentPickerRow['type']) => {
          const v1Value = v1?.value ?? '';
          const v2Value = v2?.value ?? '';
          return v1Value.localeCompare(v2Value);
        },
      },
      {
        field: 'parties',
        filterOperators: allPartyFilters,
        flex: 1,
        headerName: 'Parties',
        renderCell: ({ row }: GridRenderCellParams<DocumentPickerRow>) => {
          const partyNames = row.parties?.map(p => p.name).filter(Boolean) ?? [];
          return <Typography>{partyNames.length ? partyNames.join(', ') : '-'}</Typography>;
        },
        sortable: false,
      },
    ];

    if (!extraColumns.length) return baseCols;
    /* Used by screens like `SelectRelatedDocumentsWithRelationshipDialog` to inject a context-specific
     column (e.g. "Relationship") into the base picker table without duplicating this component.
     Insert extra columns after `extraColumnsAfterField`; if the field isn't present, append to the end. */
    const afterIdx = baseCols.findIndex(c => c.field === extraColumnsAfterField);
    const insertAt = afterIdx === -1 ? baseCols.length : afterIdx + 1;
    return [...baseCols.slice(0, insertAt), ...extraColumns, ...baseCols.slice(insertAt)];
  }, [extraColumns, extraColumnsAfterField]);

  /* Sync the DataGrid filterModel state with the picker’s initial/default grid state.
   * `useReportDataSource` owns the filterModel state, and `report.state` may not always be a Grid state shape. */
  useEffect(() => {
    setFilterModel(
      getIsGridState(mockReport.state)
        ? (mockReport.state.filter?.filterModel ?? defaultFilterModel)
        : defaultFilterModel
    );
  }, [mockReport.state, setFilterModel]);

  /* Keep the internal rowSelectionModel (a Set-based shape) in sync with the controlled prop.
   * Used by dialogs that preselect/clear selection externally (e.g. reopen with prior picks). */
  useEffect(() => {
    if (!selectedDocumentIds) return;
    setSelectedDocIds({ ids: new Set(selectedDocumentIds), type: 'include' });
  }, [selectedDocumentIds]);

  /**
   * The grid asks about every row the data source has counted, passing an empty object for
   * the ones lazy loading hasn't fetched yet (the header checkbox does this on mount).
   * Nothing is pickable from a row that isn't loaded, so answer for it here instead of
   * making each caller's predicate defend against a row with no fields.
   */
  function getRowDisabledState(row: DocumentPickerRow | undefined): RowDisabledState {
    return row?.id ? getIsRowDisabled(row) : ROW_NOT_LOADED;
  }

  return (
    <FiltersAnchorProvider setAnchorEl={setFiltersAnchorEl}>
      <div className={clsx(className, classes.listContainer)}>
        <DataGridPremium<DocumentPickerRow>
          checkboxSelection
          disableColumnMenu
          disableColumnReorder
          disableColumnResize
          disableRowSelectionExcludeModel
          lazyLoading
          pagination
          showToolbar
          apiRef={apiRef}
          className={classes.xGrid}
          columnHeaderHeight={rowHeightPx}
          columns={colDefs}
          dataSource={dataSource}
          disableMultipleRowSelection={pickerVariant === DocumentPickerVariant.SingleSelect}
          filterModel={filterModel}
          getRowClassName={params => {
            const { isRowDisabled } = getRowDisabledState(params.row);
            return isRowDisabled ? classes.rowDisabled : '';
          }}
          getRowHeight={() => 'auto'}
          hideFooter={!showDocumentCountFooter}
          initialState={{
            pagination: {
              paginationModel: { page: 0, pageSize: 50 },
            },
          }}
          isRowSelectable={({ row }) => !getRowDisabledState(row).isRowDisabled}
          rowCount={documentCount}
          rowHeight={rowHeightPx}
          rowSelectionModel={selectedDocIds}
          slotProps={{
            filterPanel: {
              filterableColumns: 'all',
            },
            footer: {
              noun: 'documents',
              totalCount: documentCount,
            },
            loadingOverlay: {
              noRowsVariant: 'linear-progress',
              variant: 'linear-progress',
            },
            panel: {
              placement: 'bottom-start',
              target: filtersAnchorEl ?? undefined,
            },
            row: { getIsRowDisabled: getRowDisabledState },
            toolbar: {
              extraToolbarContentLeft,
              extraToolbarContentRight,
              searchPlaceholder,
              showFiltersButton,
            },
          }}
          slots={{
            baseCheckbox: AlineCheckbox,
            filterPanel: ReportFiltersPanel,
            footer: ListCountFooter,
            noRowsOverlay: DocumentListEmpty,
            row: DocumentListRow,
            toolbar: DocumentListToolbar,
          }}
          sortModel={sortModel}
          sx={
            showDocumentCountFooter
              ? createDataGridPremiumPaperStyles
              : createDataGridPremiumOverrideStyles
          }
          onDataSourceError={onDataSourceError}
          onFilterModelChange={model => {
            setFilterModel(model);
          }}
          onRowSelectionModelChange={({ ids }) => {
            const allIds = [...ids].map(id => String(id));
            const lastId = allIds.at(-1);
            const newIds =
              pickerVariant === DocumentPickerVariant.MultiSelect ? allIds : lastId ? [lastId] : [];
            onSelectionChange(newIds);
            setSelectedDocIds({ ids: new Set(newIds), type: 'include' });
          }}
          onSortModelChange={setSortModel}
        />
      </div>
    </FiltersAnchorProvider>
  );
}

interface DocumentListRowProps extends GridRowProps {
  getIsRowDisabled?: NonNullable<DocumentPickerProps['getIsRowDisabled']>;
}

function DocumentListRow(props: DocumentListRowProps): JSX.Element {
  const { getIsRowDisabled = () => ROW_ENABLED, row, ...rest } = props;
  const { isRowDisabled = false, reason = '' } = row
    ? getIsRowDisabled(row as DocumentPickerRow)
    : {};

  // Need to wrap <GridRow /> in <div /> b/c <div /> can accept ref and <GridRow /> cannot.
  return (
    <AlineTooltip arrow placement="top" title={isRowDisabled ? reason : ''}>
      <div>
        <GridRow row={row} {...rest} />
      </div>
    </AlineTooltip>
  );
}

function DocumentListEmpty(): JSX.Element {
  const { classes } = useStyles();

  return (
    <ListEmpty
      icon={<DocumentSearchIcon className={classes.emptyIcon} />}
      title="No documents found"
    />
  );
}

const useToolbarStyles = makeStyles()(() => ({
  toolbar: {
    alignItems: 'center',
    display: 'flex',
    gap: 8,
    maxHeight: rowHeightPx,
    minHeight: rowHeightPx,
  },
}));

interface DocumentListToolbarProps extends GridToolbarProps {
  extraToolbarContentLeft?: React.ReactNode;
  extraToolbarContentRight?: React.ReactNode;
  searchPlaceholder?: string;
  showFiltersButton?: boolean;
}

function DocumentListToolbar({
  extraToolbarContentLeft,
  extraToolbarContentRight,
  searchPlaceholder = 'Search documents...',
  showFiltersButton = false,
}: DocumentListToolbarProps): JSX.Element | null {
  const { classes } = useToolbarStyles();
  const { search, setSearch } = useDataGridSearch();

  return (
    <div className={classes.toolbar}>
      {showFiltersButton && <FiltersToolbarButton />}
      {extraToolbarContentLeft}
      <Spacer />
      {extraToolbarContentRight}
      <SearchInput
        aria-label="Search documents"
        placeholder={searchPlaceholder}
        search={search}
        setSearch={setSearch}
      />
    </div>
  );
}

const useNameStyles = makeStyles()(theme => ({
  cell: {
    alignItems: 'center',
    display: 'flex',
    flexWrap: 'nowrap' as const,
    minWidth: 0,
    overflow: 'hidden',
  },
  title: {
    ...createClampStyles(3),
    flex: 1,
    marginLeft: theme.spacing(1),
  },
}));

function NameCell({
  row,
  value: documentName,
}: GridRenderCellParams<DocumentPickerRow, string>): JSX.Element {
  const { classes } = useNameStyles();
  const displayName = documentName || 'Untitled';

  return (
    <div className={classes.cell}>
      <StorableIcon storable={row} />
      <Typography className={classes.title} variant="body1">
        {displayName}
      </Typography>
    </div>
  );
}
